<template>
  <section v-if="presentation.visible" class="emiratisation-banner" :class="`emiratisation-banner--${presentation.severity}`" :aria-label="presentation.title">
    <div class="emiratisation-banner__summary">
      <div>
        <strong>{{ presentation.title }}</strong>
        <span class="d-block">{{ presentation.message }}</span>
      </div>
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <button type="button" class="btn btn-sm btn-link" :aria-expanded="expanded" @click="expanded = !expanded">{{ expanded ? 'Hide details' : 'View details' }}</button>
        <router-link class="btn btn-sm btn-dark" to="/emiratisation">Review Emiratisation details</router-link>
      </div>
    </div>
    <div v-if="expanded" class="emiratisation-banner__details">
      <dl class="emiratisation-banner__metrics">
        <div><dt>Applicable target</dt><dd>{{ formatNumber(summary.targetCount) }}</dd></div>
        <div><dt>Qualifying Emiratis</dt><dd>{{ summary.qualifyingCount }}</dd></div>
        <div><dt>Remaining slots</dt><dd>{{ formatNumber(summary.remainingSlots) }}</dd></div>
        <div><dt>Missing evidence</dt><dd>{{ summary.missingEvidenceTotal }}</dd></div>
        <div><dt>Next deadline</dt><dd>{{ formatDate(summary.nextDeadline) }}</dd></div>
        <div><dt>Estimated contribution</dt><dd>{{ formatMoney(summary.estimatedExposure) }}</dd></div>
      </dl>
      <p v-if="summary.estimatedExposureFormula" class="mb-2"><strong>Estimate:</strong> {{ summary.estimatedExposureFormula }}</p>
      <p v-if="summary.missingEvidenceTotal > 0" class="mb-2"><strong>Missing employee evidence:</strong> Permit {{ summary.missingPermitCount }}, pension {{ summary.missingPensionCount }}, WPS {{ summary.missingWpsCount }}, skill level {{ summary.missingSkillCount }}.</p>
      <p v-if="summary.missingSetupFields.length" class="mb-2"><strong>Establishment information needed:</strong> {{ summary.missingSetupFields.map(missingFieldLabel).join(', ') }}.</p>
      <p v-if="summary.belowMinimumWpsAmountCount > 0" class="mb-2"><strong>WPS salary advisory:</strong> {{ summary.belowMinimumWpsAmountCount }} employee{{ summary.belowMinimumWpsAmountCount === 1 ? '' : 's' }} recorded below the AED 6,000 advisory threshold. Verify the latest official WPS evidence.</p>
      <div v-if="summary.establishments.length" class="mb-2">
        <strong>MoHRE establishments:</strong>
        <ul class="mb-1">
          <li v-for="establishment in summary.establishments" :key="establishment.visaBranchId">
            {{ establishment.name }}: {{ establishment.activeEmployeeCount }} employees, target {{ formatNumber(establishment.effectiveTargetCount) }}, qualifying {{ establishment.qualifyingCount }}<span v-if="establishment.missingEvidenceTotal">, {{ establishment.missingEvidenceTotal }} needing information</span>.
          </li>
        </ul>
      </div>
      <div v-if="summary.unassignedEmployeeCount > 0" class="mb-2">
        <strong>Establishment missing:</strong> {{ summary.unassignedEmployeeCount }} employee{{ summary.unassignedEmployeeCount === 1 ? '' : 's' }} must be assigned to a Visa Branch with a MoHRE number.
        <ul v-if="summary.unassignedEmployees.length" class="mb-1">
          <li v-for="employee in summary.unassignedEmployees" :key="employee.employeeId"><router-link :to="{ path: '/employees', query: { employeeId: employee.employeeId, edit: '1' } }">{{ employee.employeeName }}<span v-if="employee.employeeCode"> ({{ employee.employeeCode }})</span></router-link></li>
        </ul>
      </div>
      <div v-if="summary.incompleteEmployees.length" class="mb-2">
        <strong>Employees needing review:</strong>
        <ul class="mb-1">
          <li v-for="employee in summary.incompleteEmployees" :key="employee.employeeId"><router-link :to="{ path: '/employees', query: { employeeId: employee.employeeId, edit: '1' } }">{{ employee.employeeName }}<span v-if="employee.employeeCode"> ({{ employee.employeeCode }})</span></router-link>: {{ employee.missingFields.map(missingFieldLabel).join(', ') }}</li>
        </ul>
        <small v-if="summary.incompleteEmployeesTruncated">Showing {{ summary.incompleteEmployees.length }} of {{ summary.incompleteEmployeeCount }} employees needing review.</small>
      </div>
      <p class="mb-2">{{ summary.disclaimer }}</p>
      <ul class="emiratisation-banner__sources mb-0" aria-label="Official Emiratisation sources">
        <li v-for="source in summary.officialSources" :key="source.url"><a :href="source.url" target="_blank" rel="noopener noreferrer">{{ source.title }}</a></li>
      </ul>
    </div>
  </section>
  <section v-else-if="loadError" class="emiratisation-banner emiratisation-banner--unavailable" role="status">
    <span>Emiratisation advisory status is temporarily unavailable.</span>
    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="load">Retry</button>
  </section>
</template>

<script>
import { emiratisationService } from '@/services/api/adminApi';
import { getErrorMessage } from '@/services/api/helpers';
import { bannerPresentation, normalizeComplianceSummary } from '@/services/emiratisationCompliance';

export default {
  name: 'EmiratisationComplianceBanner',
  data: () => ({ summary: normalizeComplianceSummary(), loading: false, loaded: false, loadError: '', expanded: false, disposed: false, generation: 0 }),
  computed: {
    presentation() { return this.loaded ? bannerPresentation(this.summary.status) : { visible: false, severity: 'none', title: '', message: '' }; },
  },
  created() {
    window.addEventListener('emiratisation-summary-refresh', this.load);
    window.addEventListener('focus', this.load);
    this.load();
  },
  beforeUnmount() {
    this.disposed = true;
    window.removeEventListener('emiratisation-summary-refresh', this.load);
    window.removeEventListener('focus', this.load);
  },
  methods: {
    formatDate(value) {
      if (!value) return 'Not available';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? 'Not available' : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
    },
    formatNumber(value) { return value === null || value === undefined ? 'Not available' : value; },
    formatMoney(value) { return value === null || value === undefined ? 'Not available' : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'AED', maximumFractionDigits: 0 }).format(Number(value) || 0); },
    missingFieldLabel(field) {
      return ({ applicability: 'MoHRE applicability', ruleCategory: 'rule category', officialTargetCount: 'official target', targetConfirmedAt: 'target confirmation date', targetedEconomicSector: 'targeted sector', mohreSelectedEstablishment: 'MoHRE selection confirmation', uaeNationalStatus: 'nationality evidence', verifiedAt: 'verification date', mohreSkillLevel: 'MoHRE skill level', hasValidWorkPermit: 'work permit', hasPensionRegistration: 'pension registration', hasWpsPayment: 'WPS payment', latestWpsPaidMonth: 'WPS paid month', latestWpsPaidAmount: 'WPS paid amount', belowMinimumWpsAmount: 'WPS amount review' })[field] || 'required information';
    },
    async load() {
      if (this.loading) return;
      const generation = ++this.generation;
      this.loading = true;
      try {
        const result = await emiratisationService.getSummary();
        if (!this.disposed && generation === this.generation) {
          this.summary = normalizeComplianceSummary(result);
          this.loaded = true;
          this.loadError = '';
        }
      } catch (error) {
        if (!this.disposed && generation === this.generation) this.loadError = getErrorMessage(error, 'Emiratisation status could not be loaded.');
      } finally {
        if (!this.disposed && generation === this.generation) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.emiratisation-banner { margin: 18px 24px 0; padding: 14px 18px; border: 1px solid; border-radius: 8px; }
.emiratisation-banner--amber { color: #5e4300; background: #fff7d6; border-color: #e5b900; }
.emiratisation-banner--red { color: #681c1c; background: #fde8e8; border-color: #dc3545; }
.emiratisation-banner--unavailable { color: #384152; background: #f4f6f8; border-color: #c9d0d8; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.emiratisation-banner__summary { display: flex; justify-content: space-between; align-items: center; gap: 18px; }
.emiratisation-banner__summary .btn-link { color: inherit; }
.emiratisation-banner__details { border-top: 1px solid currentColor; margin-top: 12px; padding-top: 12px; }
.emiratisation-banner__metrics { display: grid; grid-template-columns: repeat(6, minmax(110px, 1fr)); gap: 10px; }
.emiratisation-banner__metrics div { background: rgba(255, 255, 255, .55); border-radius: 6px; padding: 8px; }
.emiratisation-banner__metrics dt { font-size: .75rem; }
.emiratisation-banner__metrics dd { font-size: 1rem; font-weight: 700; margin: 3px 0 0; }
.emiratisation-banner__sources { display: flex; flex-wrap: wrap; gap: 6px 24px; }
@media (max-width: 767px) {
  .emiratisation-banner { margin: 12px 12px 0; }
  .emiratisation-banner__summary { align-items: stretch; flex-direction: column; }
  .emiratisation-banner__metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
